// A DÓNDE LLEGA CADA UNO AL ENTRAR (y a dónde se le manda si abre una página que no le toca).
//
// Vive fuera de App.jsx para que lo puedan usar también las páginas (App importa las páginas, así
// que importarlo desde App crearía un ciclo).
//
// LA REGLA: al entrar se aterriza en la PÁGINA DE INICIO — el resumen del día del equipo (avisos de
// Biango, quién falta esta semana, las anotaciones de la agenda y el muro). Con DOS matices:
//
//   1. LA CUENTA DEL QUIOSCO DE FICHAR (rol control_horario) NO. Es una cuenta compartida que vive
//      abierta en el tótem de la entrada, a la vista de pacientes y repartidores: nunca puede acabar
//      en una pantalla con las vacaciones y las bajas del equipo. Esa va a /fichar y punto — se mira
//      ANTES que la preferencia, para que no exista forma de configurarla fuera de ahí.
//   2. QUIEN NO LA QUIERA, QUE ELIJA OTRA. Recepción vive en "Recepción" y un doctor en su agenda:
//      imponerles un muro cada mañana es la forma de que la página de inicio les estorbe. En Mi
//      perfil se elige "Al entrar, llévame a…" y se guarda en las preferencias del usuario (viaja
//      entre ordenadores).
//
// POR QUÉ HAY UN ESPEJO EN localStorage: la preferencia es del SERVIDOR (auth/preferencias), pero
// homeFor() se llama en mitad de un redirect, donde no se puede esperar a una petición. El espejo
// se guarda POR USUARIO (dos personas comparten el ordenador de recepción: el destino de una no
// puede ser el de la otra) y quien manda sigue siendo el servidor: HomeRedirect lo pregunta al
// entrar y refresca el espejo.

export const RUTA_INICIO = '/inicio'

const clave = (usuarioId) => 'inicioPreferido:' + (usuarioId || 'anon')

// El usuario que hay en sesión, leído del navegador: home.js no puede usar hooks (se llama desde
// funciones sueltas) y necesita saber DE QUIÉN es la preferencia que lee.
function usuarioGuardado() {
  try { return JSON.parse(localStorage.getItem('user') || 'null') } catch { return null }
}

export function leerInicioPreferido(usuarioId) {
  try {
    const id = usuarioId !== undefined ? usuarioId : usuarioGuardado()?.id
    const v = localStorage.getItem(clave(id))
    // Solo rutas internas: un valor raro guardado a mano no puede sacar a nadie de la aplicación.
    return typeof v === 'string' && /^\/[A-Za-z0-9/_-]*$/.test(v) ? v : null
  } catch { return null }
}

export function guardarInicioPreferido(ruta, usuarioId) {
  try {
    const id = usuarioId !== undefined ? usuarioId : usuarioGuardado()?.id
    if (ruta) localStorage.setItem(clave(id), ruta)
    else localStorage.removeItem(clave(id))
  } catch { /* navegador sin localStorage: se usará el inicio por defecto */ }
}

// EL SUELO DE LA APLICACIÓN: a dónde va alguien al que una pantalla le dice que no.
//
// Antes cada rol tenía el suyo (colaborador→/agenda, limpieza→/limpieza, almacén→/stock). Ahora es
// la página de inicio para todos, porque es la única que TODO el equipo puede ver: así el rebote
// nunca puede caer en otra pantalla que también le diga que no. La preferencia NO se mira aquí a
// propósito — si la ruta que se acaba de denegar fuera justo la elegida como inicio, el rebote
// entraría en bucle.
export function homeFor(rol) {
  if (rol === 'control_horario') return '/fichar'   // cuenta de quiosco de fichaje (teclado PIN)
  return RUTA_INICIO
}

// LA PANTALLA ELEGIDA TIENE QUE SEGUIR SIENDO SUYA (B-127).
//
// La elección se hace entre las pantallas propias —Mi perfil filtra la lista con `moduloVisible`—,
// pero eso es una FOTO DEL DÍA QUE SE ELIGIÓ, y la preferencia dura para siempre. El caso real: la
// recepcionista pone "Almacén" como pantalla de entrada el día que lo tiene concedido; meses después
// la clínica se lo retira, y la preferencia guardada sigue mandándola ahí cada mañana. Lo que ve al
// iniciar sesión es un rebote — la ruta la expulsa a /inicio— sin que nada le explique por qué.
//
// Así que al entrar se vuelve a preguntar, con lo único que hace falta: si el permiso de ese módulo
// está DENEGADO, la preferencia ya no vale y se aterriza en la página de inicio. Es exactamente la
// condición con la que PermRoute cierra la puerta (`perms[clave] === false`), así que las dos no
// pueden discrepar.
//
// SOLO DENIEGA CON UN `false` EN LA MANO, nunca con un `undefined`: mientras los permisos no han
// llegado no se le quita a nadie su pantalla —eso sería devolver a todo el mundo a /inicio en cada
// arranque, que es el ajuste roto en la otra dirección—. Y si no se pasan `perms`, se comporta como
// siempre: quien no puede preguntar, no decide.
function sigueSiendoSuya(ruta, perms, claveDe) {
  if (!perms || typeof claveDe !== 'function') return true
  const clave = claveDe(ruta)
  return !(clave && perms[clave] === false)
}

// EL DESTINO AL ENTRAR: el que ha elegido la persona, y si no ha elegido ninguno, la página de
// inicio. El quiosco no elige. `pref` se pasa cuando ya se ha preguntado al servidor (HomeRedirect);
// si no, se usa el espejo del navegador. `perms` y `claveDe` son opcionales: con ellos se comprueba
// que la pantalla elegida siga siendo suya (ver arriba).
// PANTALLAS QUE CAMBIARON DE DIRECCIÓN. Quien eligió la de antes en «Al entrar, llévame a…» sigue llegando a la misma
// pantalla (B-833: /dashboard pasó a /recepcion el 25-sep-2026).
export const RUTAS_RENOMBRADAS = { '/dashboard': '/recepcion' }

export function inicioDe(rol, pref, perms, claveDe) {
  if (rol === 'control_horario') return '/fichar'
  const guardado = pref !== undefined ? pref : leerInicioPreferido()
  const elegido = (guardado && RUTAS_RENOMBRADAS[guardado]) || guardado
  if (elegido && /^\/[A-Za-z0-9/_-]*$/.test(elegido) && sigueSiendoSuya(elegido, perms, claveDe)) return elegido
  return RUTA_INICIO
}

// ── B-191 · EL ADMIN DE UNA CLÍNICA A MEDIO MONTAR ENTRA EN «PRIMEROS PASOS» ─────────────────────
//
// Quién decide si falta lo esencial es el SERVIDOR (GET /primeros-pasos/aterrizaje, con la misma
// cuenta que la pantalla y la marca de clínica demo). Aquí solo se decide A QUIÉN SE LE PREGUNTA,
// que es lo que se puede saber sin ir a la red, y se lleva la cuenta de «ya le hemos traído en esta
// sesión».
export const RUTA_BIENVENIDA = '/primeros-pasos?bienvenida=1'

// ¿Tiene sentido preguntar? Solo por el ADMIN REAL de la clínica en la que está:
//   · el personal de Biango NUNCA (`es_superadmin`, del usuario REAL: al «ver como» se le quita la
//     marca al usuario efectivo, y no puede ser eso lo que le abra la puerta), y menos aún mientras
//     está dentro de una clínica cliente — está ahí por otra cosa y no cuenta como usuario de ella;
//   · quien está PREVISUALIZANDO otro rol tampoco: está mirando cómo lo ve otra persona.
export function preguntarPorBienvenida({ user, realUser, visitando } = {}) {
  const real = realUser || user
  if (!real || !user) return false
  if (real.es_superadmin === true || user.es_superadmin === true) return false
  if (visitando) return false
  if (user._ver_como) return false
  return real.rol === 'admin' && user.rol === 'admin'
}

// UNA VEZ POR SESIÓN DEL NAVEGADOR. Sin esto, cada clic en el logo (que lleva a la raíz) le
// devolvería a Primeros pasos: eso es una jaula. En `sessionStorage` y no en `localStorage` a
// propósito: al volver a entrar otro día SÍ tiene que volver a caer ahí mientras falte lo esencial.
// Por usuario Y clínica: la misma cuenta puede administrar dos clínicas y la otra no está montada.
const claveVista = (usuarioId, clinicaId) => 'bienvenidaVista:' + (usuarioId || 'anon') + ':' + (clinicaId || '0')

export function bienvenidaYaVista(usuarioId, clinicaId) {
  try { return sessionStorage.getItem(claveVista(usuarioId, clinicaId)) === '1' } catch { return false }
}

export function marcarBienvenidaVista(usuarioId, clinicaId) {
  try { sessionStorage.setItem(claveVista(usuarioId, clinicaId), '1') } catch { /* sin almacenamiento: se preguntará otra vez, no pasa nada */ }
}

// EL DESTINO AL ENTRAR, entero y en un solo sitio. `bienvenida` es lo que ha contestado el servidor
// (undefined si no se le ha preguntado). La bienvenida va DELANTE de la preferencia «Al entrar,
// llévame a…»: quien elige su pantalla de entrada ya tiene la clínica en marcha, pero una clínica
// sin agendas no tiene pantalla de entrada que valga.
// B-873 · `fichar` es lo que contesta GET /control-horario/aterrizaje: si a esta persona le toca fichar AHORA (su jornada
// empieza dentro de unos minutos, o ya empezó, y todavía no ha entrado), a Fichar antes que a su pantalla de siempre.
// Detrás de la bienvenida: una clínica sin montar no tiene jornadas que fichar.
// 3-oct-2026 · EL ASISTENTE DE CONFIGURACIÓN: cuando Biango lo ha empezado para la clínica y le queda algo, el servidor
// contesta { ir: true, a: 'asistente' } y se entra en él en vez de en Primeros pasos (las mismas preguntas que el enlace).
export const RUTA_ASISTENTE = '/asistente-configuracion?bienvenida=1'
// t478 (26-oct-2026) · UNA CUENTA SOLO DEL EQUIPO DE BIANGO (vive en la clínica de la plataforma: el login lo dice con
// `cuenta_de_plataforma`) entra directa al panel de Superadmin: su «clínica» no tiene pacientes ni agenda, y lo suyo es el
// panel (el soporte, las clínicas). Una cuenta de una clínica, aunque también sea del equipo, entra en su clínica como siempre.
export const RUTA_PANEL_PLATAFORMA = '/superadmin'
export function esCuentaSoloDePlataforma(u = usuarioGuardado()) {
  return !!(u && u.es_superadmin === true && u.cuenta_de_plataforma === true)
}
export function destinoAlEntrar({ rol, preguntar, bienvenida, pref, perms, claveDe, fichar } = {}) {
  if (rol === 'control_horario') return '/fichar'
  if (esCuentaSoloDePlataforma()) return RUTA_PANEL_PLATAFORMA
  if (preguntar && bienvenida && bienvenida.ir === true) return bienvenida.a === 'asistente' ? RUTA_ASISTENTE : RUTA_BIENVENIDA
  if (fichar && fichar.ir === '/fichar') return '/fichar'
  return inicioDe(rol, pref, perms, claveDe)
}

// B-873 · LAS PORTADAS: pantallas en las que no se está escribiendo nada, así que saltar de ellas a Fichar al volver a la
// app no pierde nada (components/FicharAlVolver). En cualquier otra, en vez de saltar, se avisa.
export const PORTADAS = ['/', '/inicio', '/dashboard', '/recepcion', '/agenda', '/mi-agenda', '/comunicacion', '/whatsapp', '/conversaciones',
  '/chat', '/perfil', '/novedades', '/fichar', '/recordatorios', '/urgencias']
export function esPortada(ruta) {
  const r = String(ruta || '').split('?')[0].replace(/\/+$/, '') || '/'
  return PORTADAS.includes(r)
}

export default homeFor
